import { useCallback, useState } from "react"
import {
	ForgeRockAuthService,
	ForgeRockStep,
	resetJourneyCache
} from "@services/forgeRockAuthService"
import { ForgeRockCallback } from "@models/forgeRock.model"
import { LoginError } from "@models/error/login-error"
// import { MAX_LOGIN_ATTEMPTS } from "@utils/constants.util"
import { FRStep, FRDevice } from "@forgerock/javascript-sdk"
import {
	detectDeviceProfileCallback,
	isDeviceProfileOnlyStep,
	parseForgeRock401Error,
	isSessionTimeoutError
} from "@utils/forgeRock.util"
import { AuthService } from "@services/authService"
import { useQAMode } from "./useQAMode"

interface UseForgeRockSubmissionProps {
	currentStep: ForgeRockStep | null
	currentStepNumber: number
	callbackValues: Record<number, string>
	saveCurrentStepState: () => void
	storeStep: (
		stepNumber: number,
		step: ForgeRockStep,
		values?: Record<number, string>
	) => void
	logStepInfo: (
		action: string,
		stepNumber: number,
		step?: ForgeRockStep,
		additionalData?: Record<string, unknown>
	) => void
	logStepsHistory: () => void
	onSuccess?: () => void
	onError?: (error: Error) => void
	onStepUpdate: (
		stepNumber: number,
		step: ForgeRockStep,
		callbackValues: Record<number, string>
	) => void
}

export const useForgeRockSubmission = ({
	currentStep,
	currentStepNumber,
	callbackValues,
	saveCurrentStepState,
	storeStep,
	logStepInfo,
	logStepsHistory,
	onSuccess,
	onError,
	onStepUpdate
}: UseForgeRockSubmissionProps) => {
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [error, setError] = useState<LoginError | null>(null)
	const { isQAMode } = useQAMode()
	// Auto-submit function for DeviceProfileCallback-only steps
	const autoSubmitDeviceProfile = useCallback(async (): Promise<void> => {
		if (!currentStep || !currentStep.callbacks) {
			return
		}

		if (!isDeviceProfileOnlyStep(currentStep)) {
			return
		}

		logStepInfo(
			"AUTO_SUBMITTING_DEVICE_PROFILE",
			currentStepNumber,
			currentStep
		)

		// Save current step state before submission
		saveCurrentStepState()

		// Handle DeviceProfileCallback
		const deviceProfileCallback = detectDeviceProfileCallback(currentStep)
		if (!deviceProfileCallback) {
			setError(
				new LoginError(
					"Device profile callback not found",
					"DEVICE_PROFILE_ERROR",
					0
				)
			)
			setIsLoading(false)
			return
		}

		try {
			const { callback } = deviceProfileCallback

			// Get requirements from callback methods or output array
			let isLocationRequired = false
			let isMetadataRequired = true // Default to true if not specified

			// Try to use SDK methods first
			if (callback.isLocationRequired) {
				isLocationRequired = callback.isLocationRequired()
			} else if (callback.output) {
				// Fallback to reading from output array
				const locationOutput = callback.output.find(
					(out) => out.name === "location"
				)
				const locationValue = locationOutput?.value
				isLocationRequired =
					locationValue === "true" ||
					locationValue === 1 ||
					(typeof locationValue === "boolean" && locationValue === true)
			}

			if (callback.isMetadataRequired) {
				isMetadataRequired = callback.isMetadataRequired()
			} else if (callback.output) {
				// Fallback to reading from output array
				const metadataOutput = callback.output.find(
					(out) => out.name === "metadata"
				)
				const metadataValue = metadataOutput?.value
				isMetadataRequired =
					metadataValue === "true" ||
					metadataValue === 1 ||
					(typeof metadataValue === "boolean" && metadataValue === true) ||
					metadataOutput === undefined
			}

			// Create FRDevice instance and collect profile
			const device = new FRDevice()
			const profile = await device.getProfile({
				location: isLocationRequired,
				metadata: isMetadataRequired
			})

			// Set the profile on the callback
			if (!callback.setProfile) {
				throw new Error(
					"setProfile method not available on DeviceProfileCallback"
				)
			}

			callback.setProfile(profile)

			logStepInfo("DEVICE_PROFILE_COLLECTED", currentStepNumber, currentStep, {
				hasLocation: !!profile.location,
				hasMetadata: !!profile.metadata,
				locationRequired: isLocationRequired,
				metadataRequired: isMetadataRequired
			})
		} catch (deviceError) {
			setError(
				new LoginError(
					`Failed to collect device profile: ${deviceError instanceof Error ? deviceError.message : "Unknown error"}`,
					"DEVICE_PROFILE_ERROR",
					0
				)
			)
			setIsLoading(false)
			onError?.(deviceError as Error)
			return
		}

		setIsLoading(true)
		setError(null)

		try {
			// Submit step to ForgeRock
			// Note: The callbacks should still be the original SDK objects, so casting should work
			const nextStep = await ForgeRockAuthService.submitStep(
				currentStep as unknown as FRStep
			)

			// Check if the step contains an error in payload
			if (nextStep.type === "LoginFailure" || nextStep.payload?.code) {
				const errorPayload = nextStep.payload || {}
				const parsedError = parseForgeRock401Error(errorPayload)

				setError(
					new LoginError(
						parsedError.errorMessage,
						parsedError.errorCode,
						parsedError.remainingAttempts
					)
				)
				setIsLoading(false)
				return
			}

			const nextStepNumber = currentStepNumber + 1

			// Store the next step
			storeStep(nextStepNumber, nextStep, {})

			// Update parent component with new step
			onStepUpdate(nextStepNumber, nextStep, {})

			logStepInfo("STEP_SUBMITTED", nextStepNumber, nextStep, {
				previousStepNumber: currentStepNumber,
				isSuccess: nextStep.type === "LoginSuccess"
			})

			if (nextStep.type === "LoginSuccess") {
				logStepsHistory()
				// Don't set loading to false here - let the parent component handle the redirect
				// The form will stay in loading state until the page redirects
				onSuccess?.()
			} else {
				// Only set loading to false if we're not in success state
				setIsLoading(false)
				logStepsHistory()
			}
		} catch (error) {
			let errorMessage = "Authentication failed"
			let errorCode = "AUTHENTICATION_ERROR"
			let remainingAttempts = 0

			if (error && typeof error === "object") {
				const errorObj = error as {
					code?: string
					reason?: string
					message?: string
					response?: {
						data?: { message?: string; reason?: string; code?: string | number }
						status?: number
					}
				}

				if (errorObj.code || errorObj.reason || errorObj.message) {
					const parsedError = parseForgeRock401Error({
						code: errorObj.code,
						message: errorObj.message,
						reason: errorObj.reason
					})
					errorMessage = parsedError.errorMessage
					errorCode = parsedError.errorCode
					remainingAttempts = parsedError.remainingAttempts
				} else if (errorObj.response?.data) {
					const responseData = errorObj.response.data
					const parsedError = parseForgeRock401Error({
						code: responseData.code || errorObj.response.status,
						message: responseData.message,
						reason: responseData.reason
					})
					errorMessage = parsedError.errorMessage
					errorCode = parsedError.errorCode
					remainingAttempts = parsedError.remainingAttempts
				}
			}

			setError(new LoginError(errorMessage, errorCode, remainingAttempts))
			onError?.(error as Error)
			setIsLoading(false)
		}
	}, [
		currentStep,
		currentStepNumber,
		callbackValues,
		saveCurrentStepState,
		logStepInfo,
		storeStep,
		logStepsHistory,
		onSuccess,
		onError,
		onStepUpdate
	])

	const handleSubmit = useCallback(
		async (event: React.FormEvent): Promise<void> => {
			event.preventDefault()
			if (!currentStep || !currentStep.callbacks) {
				return
			}

			//Pre-login validation
			if (isQAMode) {
				try {
					await AuthService.preLoginCheck(callbackValues[0] as string)
				} catch (error) {
					setError(error as LoginError)
					setIsLoading(false)
					return
				}
			}

			// Check if this is a DeviceProfileCallback-only step - if so, it should be handled by autoSubmitDeviceProfile
			if (isDeviceProfileOnlyStep(currentStep)) {
				await autoSubmitDeviceProfile()
				return
			}

			logStepInfo("SUBMITTING_STEP", currentStepNumber, currentStep, {
				callbackValues: Object.keys(callbackValues).length
			})

			// Save current step state before submission
			saveCurrentStepState()

			// Handle DeviceProfileCallback before setting other callback values
			const deviceProfileCallback = detectDeviceProfileCallback(currentStep)
			if (deviceProfileCallback) {
				try {
					const { callback } = deviceProfileCallback

					// Get requirements from callback methods or output array
					let isLocationRequired = false
					let isMetadataRequired = true // Default to true if not specified

					// Try to use SDK methods first
					if (callback.isLocationRequired) {
						isLocationRequired = callback.isLocationRequired()
					} else if (callback.output) {
						// Fallback to reading from output array
						const locationOutput = callback.output.find(
							(out) => out.name === "location"
						)
						const locationValue = locationOutput?.value
						isLocationRequired =
							locationValue === "true" ||
							locationValue === 1 ||
							(typeof locationValue === "boolean" && locationValue === true)
					}

					if (callback.isMetadataRequired) {
						isMetadataRequired = callback.isMetadataRequired()
					} else if (callback.output) {
						// Fallback to reading from output array
						const metadataOutput = callback.output.find(
							(out) => out.name === "metadata"
						)
						const metadataValue = metadataOutput?.value
						isMetadataRequired =
							metadataValue === "true" ||
							metadataValue === 1 ||
							(typeof metadataValue === "boolean" && metadataValue === true) ||
							metadataOutput === undefined
					}

					// Create FRDevice instance and collect profile
					const device = new FRDevice()
					const profile = await device.getProfile({
						location: isLocationRequired,
						metadata: isMetadataRequired
					})

					// Set the profile on the callback
					callback.setProfile?.(profile)

					logStepInfo(
						"DEVICE_PROFILE_COLLECTED",
						currentStepNumber,
						currentStep,
						{
							hasLocation: !!profile.location,
							hasMetadata: !!profile.metadata,
							locationRequired: isLocationRequired,
							metadataRequired: isMetadataRequired
						}
					)
				} catch (_deviceError) {
					// Continue with submission even if device profiling fails
					// The server will handle missing device profile data
				}
			}

			// Set values on callbacks before submitting (skip output-only callbacks like TextOutputCallback)
			currentStep.callbacks.forEach(
				(callback: ForgeRockCallback, index: number) => {
					const callbackType = callback.getType?.() || callback.type

					// Skip DeviceProfileCallback as it's already handled above
					if (callbackType === "DeviceProfileCallback") {
						return
					}

					// Only set input values on callbacks that accept input
					if (callbackType !== "TextOutputCallback") {
						const value = callbackValues[index]
						if (value !== undefined) {
							callback.setInputValue?.(value)
						}
					}
				}
			)

			setIsLoading(true)
			setError(null)

			const retryWithFreshSession = async (): Promise<ForgeRockStep | null> => {
				try {
					logStepInfo("SESSION_TIMEOUT_RETRY", currentStepNumber, currentStep)
					resetJourneyCache()
					const freshStep = await ForgeRockAuthService.startJourney()

					if (!freshStep.callbacks) return null

					freshStep.callbacks.forEach(
						(callback: ForgeRockCallback, index: number) => {
							const callbackType = callback.getType?.() || callback.type
							if (
								callbackType !== "TextOutputCallback" &&
								callbackType !== "DeviceProfileCallback"
							) {
								const value = callbackValues[index]
								if (value !== undefined) {
									callback.setInputValue?.(value)
								}
							}
						}
					)

					storeStep(0, freshStep, { ...callbackValues })

					return await ForgeRockAuthService.submitStep(
						freshStep as unknown as FRStep
					)
				} catch {
					return null
				}
			}

			const handleRetryResult = (retryNextStep: ForgeRockStep): void => {
				if (
					retryNextStep.type === "LoginFailure" ||
					retryNextStep.payload?.code
				) {
					const retryPayload = retryNextStep.payload || {}
					const parsedRetryError = parseForgeRock401Error(retryPayload)
					setError(
						new LoginError(
							parsedRetryError.errorMessage,
							parsedRetryError.errorCode,
							parsedRetryError.remainingAttempts
						)
					)
					setIsLoading(false)
					return
				}

				const retryStepNumber = 1
				storeStep(retryStepNumber, retryNextStep, {})
				onStepUpdate(retryStepNumber, retryNextStep, {})

				logStepInfo(
					"SESSION_TIMEOUT_RETRY_SUCCESS",
					retryStepNumber,
					retryNextStep,
					{ isSuccess: retryNextStep.type === "LoginSuccess" }
				)

				if (retryNextStep.type === "LoginSuccess") {
					logStepsHistory()
					onSuccess?.()
				} else {
					setIsLoading(false)
					logStepsHistory()
				}
			}

			try {
				// Submit step to ForgeRock
				const nextStep = await ForgeRockAuthService.submitStep(
					currentStep as unknown as FRStep
				)

				// Check if the step contains an error in payload
				if (nextStep.type === "LoginFailure" || nextStep.payload?.code) {
					const errorPayload = nextStep.payload || {}

					if (isSessionTimeoutError(errorPayload)) {
						const retryResult = await retryWithFreshSession()
						if (retryResult) {
							handleRetryResult(retryResult)
							return
						}
					}

					const parsedError = parseForgeRock401Error(errorPayload)

					setError(
						new LoginError(
							parsedError.errorMessage,
							parsedError.errorCode,
							parsedError.remainingAttempts
						)
					)
					setIsLoading(false)
					return
				}

				const nextStepNumber = currentStepNumber + 1

				// Store the next step
				storeStep(nextStepNumber, nextStep, {})

				// Update parent component with new step
				onStepUpdate(nextStepNumber, nextStep, {})

				logStepInfo("STEP_SUBMITTED", nextStepNumber, nextStep, {
					previousStepNumber: currentStepNumber,
					isSuccess: nextStep.type === "LoginSuccess"
				})

				if (nextStep.type === "LoginSuccess") {
					logStepsHistory()
					// Don't set loading to false here - let the parent component handle the redirect
					// The form will stay in loading state until the page redirects
					onSuccess?.()
				} else {
					// Only set loading to false if we're not in success state
					setIsLoading(false)
					logStepsHistory()
				}
			} catch (error) {
				if (isSessionTimeoutError(error)) {
					const retryResult = await retryWithFreshSession()
					if (retryResult) {
						handleRetryResult(retryResult)
						return
					}
				}

				let errorMessage = "Authentication failed"
				let errorCode = "AUTHENTICATION_ERROR"
				let remainingAttempts = 0

				if (error && typeof error === "object") {
					const errorObj = error as {
						code?: string
						reason?: string
						message?: string
						response?: {
							data?: {
								message?: string
								reason?: string
								code?: string | number
							}
							status?: number
						}
					}

					if (errorObj.code || errorObj.reason || errorObj.message) {
						const parsedError = parseForgeRock401Error({
							code: errorObj.code,
							message: errorObj.message,
							reason: errorObj.reason
						})
						errorMessage = parsedError.errorMessage
						errorCode = parsedError.errorCode
						remainingAttempts = parsedError.remainingAttempts
					} else if (errorObj.response?.data) {
						const responseData = errorObj.response.data
						const parsedError = parseForgeRock401Error({
							code: responseData.code || errorObj.response.status,
							message: responseData.message,
							reason: responseData.reason
						})
						errorMessage = parsedError.errorMessage
						errorCode = parsedError.errorCode
						remainingAttempts = parsedError.remainingAttempts
					}
				}

				setError(new LoginError(errorMessage, errorCode, remainingAttempts))
				onError?.(error as Error)
				setIsLoading(false)
			}
		},
		[
			currentStep,
			currentStepNumber,
			callbackValues,
			isQAMode,
			saveCurrentStepState,
			logStepInfo,
			storeStep,
			logStepsHistory,
			onSuccess,
			onError,
			onStepUpdate
		]
	)

	return {
		handleSubmit,
		autoSubmitDeviceProfile,
		isLoading,
		error,
		setError
	}
}
